import {
	isToday as _isToday,
	type CalendarDate,
	createCalendar,
	getLocalTimeZone,
	getWeeksInMonth,
} from "@internationalized/date";
import classnames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	type CalendarProps,
	type DateValue,
	useButton,
	useCalendar,
	useCalendarCell,
	useCalendarGrid,
	useLocale,
} from "react-aria";
import { type CalendarState, useCalendarState } from "react-stately";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import sprites from "/src/shared/assets/sprite.svg";

function Calendar(props: CalendarProps<DateValue>) {
	const prevButtonRef = useRef<HTMLButtonElement | null>(null);
	const nextButtonRef = useRef<HTMLButtonElement | null>(null);

	const { locale } = useLocale();
	const state = useCalendarState({
		...props,
		locale,
		createCalendar,
	});
	const {
		calendarProps,
		prevButtonProps: _prevButtonProps,
		nextButtonProps: _nextButtonProps,
	} = useCalendar(props, state);
	const { buttonProps: prevButtonProps } = useButton(
		_prevButtonProps,
		prevButtonRef,
	);
	const { buttonProps: nextButtonProps } = useButton(
		_nextButtonProps,
		nextButtonRef,
	);

	const [selectedDate, setSelectedDate] = useState({
		year: state.focusedDate.year,
		month: state.focusedDate.month,
	});

	useEffect(() => {
		setSelectedDate({
			year: state.focusedDate.year,
			month: state.focusedDate.month,
		});
	}, [state.focusedDate]);

	const handleDateChange = (value: string, partOfDate: "year" | "month") => {
		const newValue = Number.parseInt(value, 10);
		const newDate = state.focusedDate.set({ [partOfDate]: newValue });
		state.setFocusedDate(newDate);
		state.setValue(newDate);
	};

	const generateYears = (start: number, end: number) => {
		const years = [];
		for (let i = start; i <= end; i++) {
			years.push(i);
		}
		return years;
	};

	const getNameOfMonth = (month: number) => {
		return new Date(0, month).toLocaleString("en-US", { month: "long" });
	};

	return (
		<div {...calendarProps} className="space-y-4">
			<div className="relative flex items-center justify-center pt-2">
				<Button
					{...prevButtonProps}
					ref={prevButtonRef}
					variant={"outline"}
					className={cn(
						"absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
					)}
				>
					<svg
						aria-label="chevron-left"
						role="img"
						fill="#fff"
						className="w-3 h-3"
					>
						<use xlinkHref={`${sprites}#chevron-left`} />
					</svg>
				</Button>

				<div className="flex items-center space-x-2">
					{["year", "month"].map((item) => {
						const isYear = item === "year";
						const getOptions = () => {
							return isYear
								? generateYears(1950, 2100).map((value) => ({
										value,
										label: value,
									}))
								: Array.from({ length: 12 }, (_, i) => ({
										value: i + 1,
										label: getNameOfMonth(i),
									}));
						};

						return (
							<Select
								key={item}
								value={String(isYear ? selectedDate.year : selectedDate.month)}
								onValueChange={(item) =>
									handleDateChange(item, isYear ? "year" : "month")
								}
							>
								<SelectTrigger
									className={classnames(
										"min-w-[100px] h-7 focus:ring-0 hover:bg-accent/80 transition-colors",
										{
											"min-w-[70px]": isYear,
										},
									)}
								>
									<SelectValue>
										{isYear
											? selectedDate.year
											: getNameOfMonth(selectedDate.month - 1)}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup
										className={classnames("max-h-[150px]", {
											"max-h-[100px]": isYear,
										})}
									>
										{getOptions().map(({ value, label }) => (
											<SelectItem key={value} value={String(value)}>
												{label}
											</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
						);
					})}
				</div>

				<Button
					{...nextButtonProps}
					ref={nextButtonRef}
					variant={"outline"}
					className={cn(
						"absolute right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
					)}
				>
					<svg
						aria-label="chevron-right"
						role="img"
						fill="#fff"
						className="w-3 h-3"
					>
						<use xlinkHref={`${sprites}#chevron-right`} />
					</svg>
				</Button>
			</div>

			<CalendarGrid state={state} />
		</div>
	);
}

interface CalendarGridProps {
	state: CalendarState;
}

function CalendarGrid({ state, ...props }: CalendarGridProps) {
	const { locale } = useLocale();
	const { gridProps, headerProps } = useCalendarGrid(props, state);

	const getNameOfDay = (day: number) => {
		return new Date(1970, 0, 4 + day).toLocaleString("en-US", {
			weekday: "long",
		});
	};
	const weekDays = Array.from({ length: 7 }, (_, day) => getNameOfDay(day));

	const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

	return (
		<table
			{...gridProps}
			className={cn(gridProps.className, "w-full border-collapse space-y-1")}
		>
			<thead {...headerProps}>
				<tr className="flex">
					{weekDays.map((day) => (
						<th
							className="w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground"
							key={day}
						>
							{day.charAt(0)}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{[...new Array(weeksInMonth).keys()].map((weekIndex) => (
					<tr className="flex w-full mt-1" key={weekIndex}>
						{state
							.getDatesInWeek(weekIndex)
							.map((date) =>
								date ? (
									<CalendarCell key={String(date)} state={state} date={date} />
								) : (
									<td key={`empty-${weekIndex}`} />
								),
							)}
					</tr>
				))}
			</tbody>
		</table>
	);
}

interface CalendarCellProps {
	state: CalendarState;
	date: CalendarDate;
}

function CalendarCell({ state, date }: CalendarCellProps) {
	const ref = useRef<HTMLButtonElement | null>(null);
	const {
		cellProps,
		buttonProps,
		isSelected,
		isOutsideVisibleRange,
		isDisabled,
		formattedDate,
	} = useCalendarCell({ date }, state, ref);

	const isToday = useMemo(() => {
		const timezone = getLocalTimeZone();
		return _isToday(date, timezone);
	}, [date]);

	return (
		<td
			{...cellProps}
			className={cn(
				cellProps.className,
				`relative p-0 text-center text-sm focus-within:relative focus-within:z-20 
				[&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md 
				last:[&:has([aria-selected])]:rounded-r-md`,
			)}
		>
			<Button
				{...buttonProps}
				type="button"
				variant={"ghost"}
				ref={ref}
				className={cn(
					buttonProps.className,
					"h-9 w-9",
					isToday ? "bg-accent text-accent-foreground" : "",
					isSelected
						? `bg-gold text-primary-foreground hover:bg-primary hover:text-primary-foreground 
						focus:bg-gold focus:text-primary-foreground`
						: "",
					isOutsideVisibleRange ? "text-muted-foreground opacity-50" : "",
					isDisabled ? "text-muted-foreground opacity-50" : "",
				)}
			>
				{formattedDate}
			</Button>
		</td>
	);
}

export { Calendar };
